import { useCallback, useEffect, useState } from 'react';
import { ApiError, GENERIC_FAILURE_MESSAGE, NetworkError } from '../api/httpClient';

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Traduce cualquier cosa que se rompa a un mensaje que se pueda leer.
 *
 * El servidor ya manda mensajes accionables en el sobre; se prefieren esos. Sólo cuando no hay
 * ninguno la interfaz inventa uno, y cuando el problema es la red lo dice como lo que es.
 */
export function describeError(error: unknown): string {
  if (error instanceof NetworkError) {
    return 'No hay conexión con el servidor.';
  }

  if (error instanceof ApiError) {
    // `ApiError` ya garantiza al menos un mensaje con texto, pero el `join` se protege igual: una
    // cadena vacía pinta un aviso en blanco, que es peor que uno genérico —la persona ve que algo
    // falló y no lee nada—.
    const detalle = error.messages.join(' ').trim();
    return detalle.length > 0 ? detalle : GENERIC_FAILURE_MESSAGE;
  }

  if (error instanceof Error) {
    return error.message.trim().length > 0 ? error.message : GENERIC_FAILURE_MESSAGE;
  }

  return 'Ocurrió un error inesperado.';
}

/** Una lectura que se cancela sola si la pantalla se va antes de que llegue la respuesta. */
export function useAsync<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    setNonce((value) => value + 1);
  }, []);

  /*
   * Un cambio de dependencias es OTRA lectura, y lo anterior deja de ser cierto en ese
   * instante. Sin esto, cambiar de día en la pantalla principal dejaba el encabezado diciendo
   * «10 de agosto» sobre los totales del 9 hasta que llegara la respuesta: no es un problema
   * de transición, es un número que afirma algo falso.
   *
   * `reload()` no pasa por acá a propósito. Ahí es la misma lectura otra vez —confirmaste un
   * ítem y el día se recalcula—, y conservar lo que ya está es lo que evita el parpadeo.
   */
  useEffect(() => {
    setData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setLoading(true);
    setError(null);

    load(controller.signal)
      .then((result) => {
        if (active) {
          setData(result);
        }
      })
      .catch((cause: unknown) => {
        if (cause instanceof DOMException && cause.name === 'AbortError') {
          return;
        }

        if (active) {
          setError(describeError(cause));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload };
}
